import type { PullRecord } from '@mesa/core/pull';
import { useNavigate } from '@tanstack/react-router';
import { bind, unbind } from 'mousetrap';
import { useCallback, useEffect } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { Key } from 'ts-key-enum';
import type { VListHandle } from 'virtua';

import { useCurrentOrg } from '@/services/org';
import type { Filter, FilterField } from '@/types/filters';

export function useKeyboardShortcut(
  keys: string | string[],
  callback: (e: KeyboardEvent, combo: string) => boolean | void,
  action?: string
) {
  useEffect(() => {
    bind(keys, callback, action);
    return () => {
      unbind(keys, action);
    };
  }, [callback, keys, action]);
}

// Global shortcuts that work everywhere
export function useGlobalShortcuts({ openShortcutsDialog }: { openShortcutsDialog: () => void }) {
  const navigate = useNavigate();
  const { currentOrg } = useCurrentOrg();

  useHotkeys(['h', '?'], openShortcutsDialog, { preventDefault: true, useKey: true });

  useHotkeys('g>p', () => {
    navigate({ to: '/pulls', replace: true });
  });

  useHotkeys('g>s', () => {
    navigate({ to: '/$owner/settings', params: { owner: currentOrg.account.login }, replace: true });
  });
}

export function usePullListShortcuts({
  listRef,
  items,
  selectedIndex,
  setSelectedIndex,
  onOpenFilters,
  onFocusFilters,
  enabled = true,
  searchInputRef,
}: {
  listRef: React.RefObject<VListHandle | null>;
  items: { type: 'header' | 'row'; data: PullRecord }[];
  selectedIndex: number | null;
  setSelectedIndex: React.Dispatch<React.SetStateAction<number | null>>;
  onOpenFilters?: () => void;
  onFocusFilters?: () => void;
  enabled?: boolean;
  searchInputRef?: React.RefObject<HTMLInputElement | null>;
}) {
  const navigate = useNavigate();

  useHotkeys('/', () => searchInputRef?.current?.focus(), { preventDefault: true, useKey: true });

  // Move to next row, skipping headers
  useHotkeys(['j', 'ctrl+n'], () => {
    if (!enabled || items.length === 0 || !listRef.current) return;
    setSelectedIndex((prev) => {
      const start = prev ?? -1;
      const nextRowIndex = items.findIndex((item, idx) => idx > start && item.type === 'row');
      if (nextRowIndex !== -1) {
        listRef.current?.scrollToIndex(nextRowIndex, { offset: -40 });
        return nextRowIndex;
      }
      return prev;
    });
  });

  // Move to previous row, skipping headers (no wrap-around)
  useHotkeys(['k', 'ctrl+p'], () => {
    if (!enabled || items.length === 0 || !listRef.current) return;
    setSelectedIndex((prev) => {
      if (prev == null) return null;
      // search upwards for the nearest row
      for (let i = prev - 1; i >= 0; i--) {
        if (items[i].type === 'row') {
          listRef.current?.scrollToIndex(i, { offset: -40 });
          return i;
        }
      }
      // no prior row found, stay on current row
      return prev;
    });
  });

  // Activate selected PR on Enter
  useHotkeys('enter', (e) => {
    if (!enabled || selectedIndex == null) return;
    const item = items[selectedIndex];
    if (!item || item.type !== 'row') return;
    e.preventDefault();
    const pr = item.data;
    navigate({
      to: '/$owner/$repo/pull/$number',
      params: {
        owner: pr.owner,
        repo: pr.repo,
        number: pr.number,
      },
    });
  });

  // Open filter dropdown
  useHotkeys('f', (e) => {
    if (!enabled) return;
    e.preventDefault();
    onOpenFilters?.();
  });

  // Focus first filter badge
  useHotkeys('shift+f', (e) => {
    if (!enabled) return;
    e.preventDefault();
    onFocusFilters?.();
  });
}

/**
 * KEYBOARD SHORTCUTS FOR PULL REQUEST FILTERS
 * ==========================================
 *
 * Opening Filters:
 * "f" - Open filter dropdown (from pull list)
 *
 * When Filter Dropdown is Open:
 *
 * Main Menu Navigation:
 * "c" - Clear all filters
 * "s" - Open State submenu
 * "r" - Open Repository submenu
 * "a" - Open Author submenu
 * "g" - Open Reviewer submenu
 * "m" - Open Ready to Merge submenu
 *
 * State Submenu:
 * "o" - Toggle OPEN state
 * "d" - Toggle DRAFT state
 * "m" - Toggle MERGED state
 * "c" - Toggle CLOSED state
 *
 * Note: Filter shortcuts are only active when the filter dropdown is open.
 */
export function usePullListFilterShortcuts({
  filters,
  setFilters,
  enabled = true,
  openSubmenu,
  setOpenSubmenu,
  onOpenStateSubmenu,
  onOpenRepoSubmenu,
  onOpenAuthorSubmenu,
  onOpenReviewerSubmenu,
  onOpenReadyToMergeSubmenu,
}: {
  filters: Filter[];
  setFilters: (filters: Filter[] | ((filters: Filter[]) => Filter[])) => void;
  enabled?: boolean;
  openSubmenu?: 'state' | 'repo' | 'author' | 'reviewer' | 'readyToMerge' | null;
  setOpenSubmenu?: (submenu: 'state' | 'repo' | 'author' | 'reviewer' | 'readyToMerge' | null) => void;
  onOpenStateSubmenu?: () => void;
  onOpenRepoSubmenu?: () => void;
  onOpenAuthorSubmenu?: () => void;
  onOpenReviewerSubmenu?: () => void;
  onOpenReadyToMergeSubmenu?: () => void;
}) {
  const toggleFilterValue = useCallback(
    (field: FilterField, value: string) => {
      if (!enabled) return;
      const existingFilter = filters.find((f) => f.field === field);
      if (!existingFilter) {
        setFilters((prev) => [...prev, { field, operation: 'IN', values: [value] }]);
        return;
      }

      if (existingFilter.values.includes(value)) {
        setFilters((prev) =>
          prev.map((f) => (f.field === field ? { ...f, values: f.values.filter((v) => v !== value) } : f))
        );
        return;
      }

      setFilters((prev) => prev.map((f) => (f.field === field ? { ...f, values: [...f.values, value] } : f)));
    },
    [filters, setFilters, enabled]
  );

  // Main menu shortcuts
  useHotkeys('c', () => {
    if (!enabled || openSubmenu) return;
    setFilters([]);
  });

  useHotkeys('s', () => {
    if (!enabled || openSubmenu) return;
    onOpenStateSubmenu?.();
  });

  useHotkeys('r', () => {
    if (!enabled || openSubmenu) return;
    onOpenRepoSubmenu?.();
  });

  useHotkeys('a', () => {
    if (!enabled || openSubmenu) return;
    onOpenAuthorSubmenu?.();
  });

  useHotkeys('g', () => {
    if (!enabled || openSubmenu) return;
    onOpenReviewerSubmenu?.();
  });

  useHotkeys('m', () => {
    if (!enabled || openSubmenu) return;
    onOpenReadyToMergeSubmenu?.();
  });

  // Handle Enter key to prevent bubbling to pull list
  useHotkeys('enter', (e) => {
    if (!enabled) return;
    // Prevent the pull list enter handler from firing
    e.preventDefault();
    e.stopPropagation();
    // Let the dropdown menu handle the enter key naturally
    return false;
  });

  // State submenu shortcuts
  useHotkeys('o', () => {
    if (!enabled || openSubmenu !== 'state') return;
    toggleFilterValue('state', 'OPEN');
  });

  useHotkeys('d', () => {
    if (!enabled || openSubmenu !== 'state') return;
    toggleFilterValue('state', 'DRAFT');
  });

  useHotkeys('m', () => {
    if (!enabled || openSubmenu !== 'state') return;
    toggleFilterValue('state', 'MERGED');
  });

  useHotkeys('c', () => {
    if (!enabled || openSubmenu !== 'state') return;
    toggleFilterValue('state', 'CLOSED');
  });
}
